import { Grid, Stack, Typography } from '@mui/material';
import { FormSection } from 'Components';
import { Dictionary } from 'Dictionary';
import { CASE_TYPE_RELATION_ID, DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary, useDispatchApp } from 'Hooks';
import { useContext, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { IndexContext } from '../CaseContext';
import { CaseDetailsSlice, setCaseType, store } from 'Store';
import { CheckboxGroup } from './CheckboxGroup';
import { InjuryTraumaQuestionnaire } from './InjuryTraumaQuestionnaire';
import { DiseaseMentalQuestionnaire } from './DiseaseMentalQuestionnaire';
import { ICaseDetailsInputs } from 'API';

export const SecondSection = () => {
  const [caseTypes] = useAppSelector(
    ({
      generalContent: {
        generalContent: { caseTypes, investigationDirectorOptions },
      },
    }) => [caseTypes, investigationDirectorOptions]
  );

  const { index, caseTypeName = '' } = useContext(IndexContext);
  const dispatch = useDispatchApp();

  const {
    formState: { errors },
  } = useFormContext<ICaseDetailsInputs>();

  const caseTypeTextRef = useRef<any>(null);

  const dictionary = useDictionary(Dictionary, DICTIONARY.CASE_DETAILS);

  const baseName = `caseDetails.${index}`;

  const caseType = useWatch({
    name: caseTypeName,
    defaultValue: 0,
  }) as number;

  useEffect(() => {
    dispatch(setCaseType({ id: index, type: caseType }));
  }, [caseType, index, dispatch]);

  const caseTypeSection = useWatch({
    name: `${baseName}.caseTypeSection`,
  });

  useEffect(() => {
    if (!caseTypeSection) return;
    const injuryType = caseTypeSection.isInjury || caseTypeSection.isPostTrauma;
    const isDiseaseMental =
      caseTypeSection.isDisease || caseTypeSection.isMentalIllness;
    if (injuryType && isDiseaseMental) {
      store.dispatch(
        CaseDetailsSlice.actions.setCaseType({
          id: index,
          type: CASE_TYPE_RELATION_ID.ALL,
        })
      );
    } else if (injuryType) {
      store.dispatch(
        CaseDetailsSlice.actions.setCaseType({
          id: index,
          type: CASE_TYPE_RELATION_ID.INJURY_ID,
        })
      );
    } else if (isDiseaseMental) {
      store.dispatch(
        CaseDetailsSlice.actions.setCaseType({
          id: index,
          type: CASE_TYPE_RELATION_ID.MENTAL_ID,
        })
      );
    } else {
      store.dispatch(
        CaseDetailsSlice.actions.setCaseType({
          id: index,
          type: 0,
        })
      );
    }
  }, [caseTypeSection, index]);

  useEffect(() => {
    if (errors.caseDetails && errors.caseDetails[index]?.caseTypeSection) {
      if (caseTypeTextRef.current) caseTypeTextRef.current.focus();
    }
  }, [errors.caseDetails, index]);

  const caseTypeSectionError = errors.caseDetails
    ? errors.caseDetails[index]?.caseTypeSection
    : undefined;

  const formFieldNames = [
    'isInjury',
    'isDisease',
    'isPostTrauma',
    'isMentalIllness',
  ];

  const modifiedCaseTypes =
    caseTypes.length > 0
      ? [caseTypes[0], caseTypes[1], caseTypes[3], caseTypes[2]]
      : [];

  return (
    <FormSection grouped id={dictionary.secondSectionTitle}>
      <span className={'sr-only'}>{dictionary.secondSectionTitle}</span>
      <Stack ref={caseTypeTextRef} tabIndex={-1}>
        <Typography
          sx={{
            fontWeight: 'bold',
            color: caseTypeSectionError ? 'red' : undefined,
          }}
        >
          {dictionary.secondSectionTitle}
        </Typography>
        <Stack gap='6px'>
          <Typography sx={{ color: caseTypeSectionError ? 'red' : undefined }}>
            {dictionary.secondSectionTitleIntro1}
          </Typography>
          <Typography sx={{ color: caseTypeSectionError ? 'red' : undefined }}>
            {dictionary.secondSectionTitleIntro2}
          </Typography>
        </Stack>
      </Stack>
      <CheckboxGroup
        name={`${baseName}.caseTypeSection`}
        values={formFieldNames}
        options={modifiedCaseTypes.map(({ id, value }) => ({
          value: id.toString(),
          label: value,
        }))}
      />
      {caseTypeSection?.isPostTrauma && (
        <Grid
          role='note'
          sx={{
            background: '#F2F9FF',
            borderRadius: '12px',
            padding: '16px',
            mb: 4,
          }}
        >
          <Typography sx={{ fontWeight: 'bold' }}>
            {dictionary.secondSectionMentalBoxTitle}
          </Typography>
          <Typography>{dictionary.secondSectionMentalBoxInfo}</Typography>
        </Grid>
      )}
      {(caseTypeSection?.isInjury || caseTypeSection?.isPostTrauma) && (
        <InjuryTraumaQuestionnaire />
      )}
      {(caseTypeSection?.isDisease || caseTypeSection?.isMentalIllness) && (
        <DiseaseMentalQuestionnaire />
      )}
    </FormSection>
  );
};
